import type { ComponentProps, ReactNode } from "react";
import { ListItemIcon, type ListItemIconName } from "@/components/icons";
import { cn } from "@/lib/utils";

type ListItemProps = {
  variant?: "default" | "tall";
  icon?: ListItemIconName;
  customIconUrl?: string;
  iconBackground?: string;
  title?: string;
  subtitle?: string;
  children?: ReactNode;
} & ComponentProps<"div">;

const IconImage = ({ src }: { src: string }) => (
  <img src={src} alt="" className="size-6" />
);

const ListItem = ({
  variant = "default",
  icon,
  customIconUrl,
  iconBackground = "bg-sand-50",
  title,
  subtitle,
  children,
  className,
  ...rest
}: ListItemProps) => {
  const hasIcon = customIconUrl || icon;

  if (variant === "tall") {
    const hasContent = title || subtitle;

    return (
      <div className={cn("flex items-center [&>*+*]:ml-3", className)} {...rest}>
        {hasIcon && (
          <div className={cn("rounded-full p-3 shrink-0", iconBackground)}>
            {customIconUrl ? (
              <IconImage src={customIconUrl} />
            ) : (
              <ListItemIcon name={icon!} className="size-6" />
            )}
          </div>
        )}
        {hasContent && (
          <div className="flex flex-col flex-1">
            {title && <span className="text-copy-bold">{title}</span>}
            {subtitle && <span className="text-copy">{subtitle}</span>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center [&>*+*]:ml-3", className)} {...rest}>
      {hasIcon && (
        customIconUrl ? (
          <IconImage src={customIconUrl} />
        ) : (
          <ListItemIcon name={icon!} className="size-6 shrink-0" />
        )
      )}
      <span className="flex-1 text-copy">{children}</span>
    </div>
  );
};

export { ListItem };
